import { BigNumber } from 'bignumber.js';
import { CSSProperties, FocusEvent, Ref, useCallback } from 'react';
import { cleanValue, parseValueForInput } from '~/utils/numberInputs';

interface Props {
  ariaLabel: string;
  autoFocus?: boolean;
  className?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  maxDecimalPlaces?: number;
  name?: string;
  onChange?: (value: string) => void;
  onlyPositive?: boolean;
  ref?: Ref<HTMLInputElement>;
  style?: CSSProperties;
  value: string;
}

const AmountInput: React.FC<Props> = ({
  ariaLabel,
  autoFocus,
  className,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  name,
  onChange,
  onlyPositive = false,
  ref,
  style,
  value,
}) => {
  const handleChange = useCallback(
    (newValue: string) => {
      const cleanedValue = cleanValue(newValue, onlyPositive);
      onChange?.(cleanedValue);
    },
    [onChange, onlyPositive],
  );

  const handleBlur = (e: FocusEvent<Element, Element>) => {
    const inputEl = e.target as HTMLInputElement;
    if (inputEl.value !== '') {
      const cleanedValue = cleanValue(inputEl.value, onlyPositive);

      const numberValue = BigNumber(cleanedValue);
      onChange?.(numberValue.toFixed(2));
    }
  };

  return (
    <input
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      className={className}
      disabled={isDisabled}
      data-invalid={isInvalid}
      readOnly={isReadOnly}
      required={isRequired}
      name={name}
      onBlur={handleBlur}
      onChange={(e) => handleChange(e.target.value)}
      style={style}
      value={parseValueForInput(value)}
      ref={ref}
    />
  );
};

export default AmountInput;
